import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  CONTRACT_STATUSES,
  type ContractStatus,
  type ContractSummary,
  type TemplateSummary,
} from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate } from '../lib/format';

export function ContractsPage() {
  const { fetcher } = useAuth();
  const [status, setStatus] = useState<ContractStatus | ''>('');
  const [templateId, setTemplateId] = useState('');

  const tplQ = useQuery({
    queryKey: ['templates'],
    queryFn: () => fetcher<{ templates: TemplateSummary[] }>('/api/templates'),
  });
  const q = useQuery({
    queryKey: ['contracts', status],
    queryFn: () =>
      fetcher<{ contracts: ContractSummary[] }>(
        status ? `/api/contracts?status=${status}` : '/api/contracts',
      ),
  });

  const rows = (q.data?.contracts ?? []).filter((c) =>
    templateId ? c.template_id === templateId : true,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Contracts</h1>
        <Link to="/contracts/new" className="btn-primary">
          New contract
        </Link>
      </div>
      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Status</label>
          <select
            className="input mt-1"
            value={status}
            onChange={(e) => setStatus(e.target.value as ContractStatus | '')}
          >
            <option value="">All</option>
            {CONTRACT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Template</label>
          <select
            className="input mt-1"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">All</option>
            {(tplQ.data?.templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="card overflow-x-auto">
        {q.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500">No contracts.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1">Contract</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Created</th>
                <th className="px-2 py-1">Sent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <Link
                      to={`/contracts/${c.id}`}
                      className="text-brand hover:underline"
                    >
                      {c.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-2 py-1">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    {formatDate(c.created_at)}
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    {formatDate(c.sent_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
